import {
  ComponentsActionTypes,
  SET_ERROR,
  SET_COMPONENTS,
  SET_COMPONENT,
  SET_COMPONENT_SECTION,
  DELETE_COMPONENT_SECTION,
  SET_FETCHED_COMPONENT,
  ADD_DIGITALLY_RECEIVED,
  DELETE_SERIES_SUBMISSION_WINDOW,
  DELETE_SECTION,
  DELETE_ALL_SECTIONS,
  GET_COMPONENTS,
  DELETE_ALL_COMPONENTS,
  SET_IS_LOADING,
  SET_IS_REPORT_BEING_GENERATED,
} from './types';

import { Component, Section, ComponentsState } from '../../types/Components';
import { RESET_GLOBAL_STATE, GlobalActionTypes } from '../types';
import { unknown } from '../actions';
import { Series } from '@cambridgeassessment/lib-ee-commons';

export const initialState: ComponentsState = {
  components: undefined,
  isReportBeingGenerated: false,
  component: undefined,
  isLoading: false,
  hasError: false,
};

const removeSubmissionWindowFromComponentSeries = (
  component: Component,
  seriesId: Series['id'],
  field: 'submissionWindow' | 'marksSubmissionWindow',
): Component => {
  let updatedSeries;

  switch (field) {
    case 'submissionWindow': {
      updatedSeries = component.series.map(({ submissionWindow, ...seriesProperties }) =>
        seriesProperties.id === seriesId ? seriesProperties : { ...seriesProperties, submissionWindow },
      );
      break;
    }
    case 'marksSubmissionWindow': {
      updatedSeries = component.series.map(({ marksSubmissionWindow, ...seriesProperties }) =>
        seriesProperties.id === seriesId ? seriesProperties : { ...seriesProperties, marksSubmissionWindow },
      );
      break;
    }
    default: {
      updatedSeries = component.series;
    }
  }

  return {
    ...component,
    series: updatedSeries,
  };
};

export default (
  state = initialState,
  action: ComponentsActionTypes | GlobalActionTypes = unknown(),
): ComponentsState => {
  switch (action.type) {
    case SET_IS_LOADING: {
      const {
        payload: { value },
      } = action;

      return { ...state, isLoading: value };
    }
    case GET_COMPONENTS: {
      return { ...state, isLoading: true };
    }

    case SET_COMPONENTS: {
      const {
        payload: { components },
      } = action;
      components.sort((a, b) => parseInt(a.number) - parseInt(b.number));

      return {
        ...state,
        components,
        isLoading: false,
        hasError: false,
      };
    }
    case SET_FETCHED_COMPONENT: {
      const {
        payload: { component },
      } = action;

      return {
        ...state,
        component,
        isLoading: false,
        hasError: false,
      };
    }
    case SET_COMPONENT: {
      const {
        payload: { componentId, component },
      } = action;
      const components = [...(state.components || [])];

      const componentIndex = components.findIndex((item: Component) => item._id === componentId);
      const { sections } = component;

      components.splice(componentIndex, 1, { ...component, sections: [...(sections || [])] });

      return {
        ...state,
        components,
        component,
        isLoading: false,
        hasError: false,
      };
    }

    case SET_COMPONENT_SECTION: {
      const {
        payload: { componentId, component },
      } = action;
      const components = [...(state.components || [])];

      const componentIndex = components.findIndex((item: Component) => item._id === componentId);
      const { componentSections } = component;

      components.splice(componentIndex, 1, { ...component, componentSections: [...(componentSections || [])] });

      return {
        ...state,
        components,
        component,
        isLoading: false,
        hasError: false,
      };
    }
    case ADD_DIGITALLY_RECEIVED: {
      const {
        payload: { componentId, sectionData },
      } = action;

      const components = [...(state.components || [])];
      const componentIndex = components.findIndex((component: Component) => component._id === componentId);
      const component = components[componentIndex];
      if (component) {
        component.sections = [sectionData] as Section[];
      }

      return {
        ...state,
        components,
        component,
        isLoading: false,
        hasError: false,
      };
    }
    case DELETE_SECTION: {
      const {
        payload: { componentId, id },
      } = action;
      const components = state.components;
      let component = state.component;

      if (components) {
        const componentIndex = components.findIndex((item: Component) => item._id === componentId);
        component = components[componentIndex];
        const { sections = [] } = component;

        const sectionToRemove = sections.findIndex((section: Section) => section.id === id);

        sections.splice(sectionToRemove, 1);
      }

      return {
        ...state,
        components,
        component,
        isLoading: false,
        hasError: false,
      };
    }

    case DELETE_COMPONENT_SECTION: {
      const {
        payload: { componentId, id },
      } = action;
      const components = state.components;
      let component = state.component;

      if (components) {
        const componentIndex = components.findIndex((item: Component) => item._id === componentId);
        component = components[componentIndex];
        const { componentSections = [] } = component;

        const sectionToRemove = componentSections.findIndex((section: Section) => section.id === id);

        componentSections.splice(sectionToRemove, 1);
      }

      return {
        ...state,
        components,
        component,
        isLoading: false,
        hasError: false,
      };
    }
    case DELETE_SERIES_SUBMISSION_WINDOW: {
      const {
        payload: { componentId, id, field },
      } = action;

      const updatedComponents = state.components?.map((component) =>
        component._id !== componentId ? component : removeSubmissionWindowFromComponentSeries(component, id, field),
      );

      const updatedComponent =
        state.component && state.component._id === componentId
          ? removeSubmissionWindowFromComponentSeries(state.component, id, field)
          : state.component;

      return {
        ...state,
        components: updatedComponents,
        component: updatedComponent,
        isLoading: false,
        hasError: false,
      };
    }
    case DELETE_ALL_SECTIONS: {
      const {
        payload: { componentId },
      } = action;
      const components = state.components;

      if (components) {
        const componentIndex = components.findIndex((item: Component) => item._id === componentId);
        const component = components[componentIndex];
        component.sections = [];
      }

      return {
        ...state,
        components,
        isLoading: false,
        hasError: false,
      };
    }
    case DELETE_ALL_COMPONENTS: {
      return {
        ...state,
        components: undefined,
      };
    }

    case SET_IS_REPORT_BEING_GENERATED: {
      const {
        payload: { isReportBeingGenerated },
      } = action;

      return {
        ...state,
        isReportBeingGenerated,
      };
    }

    case SET_ERROR: {
      return {
        ...state,
        hasError: true,
        isLoading: false,
      };
    }

    case RESET_GLOBAL_STATE: {
      return initialState;
    }

    default:
      return state;
  }
};
